/**
 * Shared "can this unit initiate an attack?" rules (aligned with OrderBehavior.validateAttack).
 * Used by OrderDesign (zZz / affordance) and BattleUI keyboard shortcuts.
 *
 * Out of Reach (isOOR) limits who can target this unit in combat; it does not prevent this unit
 * from being the attacker (reposition flank / deploy flank sets isOOR while attacks remain valid).
 *
 * @param {object|null} order
 * @param {object|null} gameState
 * @param {string|null} zoneName e.g. "your_front", "opponent_flank"
 * @param {{ activePlayerId?: string, opponentPlayerId?: string }} [options] Viewer POV ids for unit 240 fallback when instanceId lookup fails.
 * @returns {boolean} true if the unit cannot initiate an attack
 */

const DIVINE_SIEGE_ENGINE_ID = 240;
const SIEGE_ENGINE_ZONE_FULL_COUNT = 5;

function isConfusedUnit(order) {
  return order?.confused === true || order?.isConfused === true;
}

/** Match OrderBehavior.validateAttack: count non-null units in the row that contains this instance. */
function countFriendlyZoneUnitsForOrder(order, gameState) {
  if (!order?.instanceId || !gameState?.state) {
    return null;
  }
  const allPlayerIds = gameState.players || Object.keys(gameState.state);
  for (const playerId of allPlayerIds) {
    const player = gameState.state[playerId];
    if (!player?.playedOrders) continue;
    for (const zone of ['front', 'flank']) {
      const zoneUnits = player.playedOrders[zone] || [];
      const inZone = zoneUnits.some((u) => u && u.instanceId === order.instanceId);
      if (inZone) {
        return zoneUnits.filter((u) => u !== null).length;
      }
    }
  }
  return null;
}

function resolvePlayerIdForZoneLabel(zoneName, gameState, options) {
  const allPlayerIds = gameState.players || Object.keys(gameState.state || {});
  if (zoneName.startsWith('your_')) {
    if (options.activePlayerId) return options.activePlayerId;
    return allPlayerIds.find((id) => id != null) || null;
  }
  if (zoneName.startsWith('opponent_')) {
    if (options.opponentPlayerId) return options.opponentPlayerId;
    if (options.activePlayerId) {
      const opponent = allPlayerIds.find((id) => String(id) !== String(options.activePlayerId));
      if (opponent) return opponent;
    }
    return allPlayerIds.length > 1 ? allPlayerIds[1] : allPlayerIds[0] || null;
  }
  return null;
}

function hasBlitzAbility(order) {
  if (!order) return false;
  // Check instance-level abilities first. Some server paths may provide
  // instanceAbilities without setting hasInstanceModifications.
  if (Array.isArray(order.instanceAbilities)) {
    const hasInstanceBlitz = order.instanceAbilities.some(
      (ability) => typeof ability === 'string' && ability.toLowerCase() === 'blitz'
    );
    if (hasInstanceBlitz) {
      return true;
    }
  }
  if (Array.isArray(order.abilities)) {
    return order.abilities.some(
      (ability) => typeof ability === 'string' && ability.toLowerCase() === 'blitz'
    );
  }
  if (typeof order.ability === 'string') {
    return order.ability.toLowerCase() === 'blitz';
  }
  return false;
}

export function unitCannotInitiateAttack(order, gameState, zoneName, options = {}) {
  // Match legacy OrderDesign: no state → do not mark as "cannot attack" (e.g. Strategy hand without gameState)
  if (!order || !gameState) {
    return false;
  }

  const immobilized =
    order.isImmobilized === true || order.immobilized === true || order.status === 'immobilized';
  if (immobilized) {
    return true;
  }

  const currentTurn =
    gameState.turnNumber !== undefined && gameState.turnNumber !== null
      ? gameState.turnNumber
      : gameState.turn;

  if (order.canAttack === false) {
    return true;
  }

  const unitHasBlitz = hasBlitzAbility(order);

  if (order.playedTurn !== undefined && order.playedTurn !== null && !unitHasBlitz) {
    const turnToCompare =
      gameState.turnNumber !== undefined && gameState.turnNumber !== null
        ? gameState.turnNumber
        : currentTurn;
    if (order.playedTurn === turnToCompare) {
      return true;
    }
  } else {
    if (order.canAttackThisTurn === false && !unitHasBlitz) {
      return true;
    }
  }

  if (order.lastAttackTurn !== undefined && order.lastAttackTurn !== null) {
    const turnToCompare =
      gameState.turnNumber !== undefined && gameState.turnNumber !== null
        ? gameState.turnNumber
        : currentTurn;
    if (order.lastAttackTurn === turnToCompare) {
      if ((order.extraAttacks && order.extraAttacks > 0) || order.quantumSurgeAttacks) {
        const attackCount = order.attackCount || 0;
        const totalAllowedAttacks = 1 + (order.extraAttacks || 0) + (order.quantumSurgeAttacks || 0);
        if (attackCount < totalAllowedAttacks) {
          return false;
        }
      }
      if (order.canAttackAgain) {
        return false;
      }
      return true;
    }
  }

  if (Number(order.id) === DIVINE_SIEGE_ENGINE_ID && !isConfusedUnit(order) && gameState.state) {
    let unitCount = countFriendlyZoneUnitsForOrder(order, gameState);
    if (unitCount == null && zoneName) {
      const zone = zoneName.includes('front') ? 'front' : 'flank';
      const playerId = resolvePlayerIdForZoneLabel(zoneName, gameState, options);
      if (playerId && gameState.state[playerId]?.playedOrders?.[zone]) {
        const zoneUnits = gameState.state[playerId].playedOrders[zone] || [];
        unitCount = zoneUnits.filter((u) => u !== null).length;
      }
    }
    if (unitCount != null && unitCount < SIEGE_ENGINE_ZONE_FULL_COUNT) {
      return true;
    }
  }

  return false;
}
